/**
 * Navigation Hook for Permission-based UI
 *
 * Provides navigation filtering and access control based on user feature permissions.
 */

import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  NavigationGroup,
  NavigationItem,
  NAVIGATION_GROUPS,
  CORE_NAVIGATION,
  QUICK_ACCESS_ITEMS
} from '../config/navigationConfig';
import {
  hasAnyFeaturePermission,
  hasAllFeaturePermissions
} from '../utils/permissionUtils';
import { FeaturePermission } from '../constants/featurePermissions';

// Extended navigation item with lock state
export interface NavigationItemWithLock extends NavigationItem {
  isLocked?: boolean;
  canAccess?: boolean;
}

// Hook return type
export interface NavigationHookResult {
  // Filtered navigation data
  accessibleGroups: NavigationGroup[];
  accessibleItems: NavigationItemWithLock[];
  coreNavigation: NavigationItemWithLock[];
  quickAccessItems: NavigationItemWithLock[];

  // Navigation state
  totalAccessibleItems: number;

  // Utility functions
  canAccessItem: (item: NavigationItem) => boolean;
  canAccessGroup: (group: NavigationGroup) => boolean;
  getItemAccessLevel: (itemId: string) => 'none' | 'read' | 'write';

  // Navigation organization
  groupedNavigation: NavigationGroup[];
  flatNavigation: NavigationItemWithLock[];
}

/**
 * Main navigation hook
 */
export const useNavigation = (): NavigationHookResult => {
  const { user, currentCompanyUser } = useSelector((state: RootState) => state.auth);
  const { currentCompany } = useSelector((state: RootState) => state.companies);

  // Get user's feature permissions
  const userPermissions: FeaturePermission[] = useMemo(() => {
    // Super admin has all permissions because no company for superadmin
    if (user?.role === 'super_admin') {
      return Object.values({
        PROPERTIES_READ: 'properties_read',
        PROPERTIES_WRITE: 'properties_write',
        RENTALUNIT_READ: 'rentalunit_read',
        RENTALUNIT_WRITE: 'rentalunit_write',
        TENANTS_READ: 'tenants_read',
        TENANTS_WRITE: 'tenants_write',
        BOOKINGS_READ: 'bookings_read',
        BOOKINGS_WRITE: 'bookings_write',
        PAYMENTS_READ: 'payments_read',
        PAYMENTS_WRITE: 'payments_write',
        INVOICES_READ: 'invoices_read',
        INVOICES_WRITE: 'invoices_write',
        MAINTENANCE_READ: 'maintenance_read',
        MAINTENANCE_WRITE: 'maintenance_write',
        MAINTENANCE_ASSIGN: 'maintenance_assign',
        COMPANY_READ: 'company_read',
        COMPANY_WRITE: 'company_write',
        USERS_READ: 'users_read',
        USERS_WRITE: 'users_write',
        ANALYTICS_READ: 'analytics_read',
        REPORTS_READ: 'reports_read',
        ANALYTICS_EXPORT: 'analytics_export',
        ADMIN_READ: 'admin_read',
        ADMIN_WRITE: 'admin_write',
        NOTIFICATIONS_READ: 'notifications_read',
        NOTIFICATIONS_WRITE: 'notifications_write'
      }) as FeaturePermission[];
    }

    return (currentCompanyUser?.featurePermissions as FeaturePermission[]) || [];
  }, [user, currentCompanyUser?.featurePermissions]);

  // Get company features list
  const companyFeatures = useMemo(() => {
    return (currentCompany?.featuresList as FeaturePermission[]) || [];
  }, [currentCompany?.featuresList]);

  // Function to check if user can access a navigation item
  const canAccessItem = useMemo(() => {
    return (item: NavigationItem): boolean => {

      if (item.requiredFeatures.length === 0) {
        return true;
      }

      // Use feature permissions only
      if (item.requireAll) {
        return hasAllFeaturePermissions(userPermissions, item.requiredFeatures);
      } else {
        return hasAnyFeaturePermission(userPermissions, item.requiredFeatures);
      }
    };
  }, [userPermissions, user]);

  // Function to check if item is locked by company subscription
  const isItemLocked = useMemo(() => {
    return (item: NavigationItem): boolean => {
      // Skip if not configured to show lock
      if (!item.showLock) return false;

      // Skip if user doesn't have permission anyway
      if (!canAccessItem(item)) return false;

      // Skip for super admin
      if (user?.role === 'super_admin') return false;

      // Check if company has the required features
      if (companyFeatures.length === 0) return false;

      if (item.requireAll) {
        return !item.requiredFeatures.every(feature => companyFeatures.includes(feature));
      } else {
        return !item.requiredFeatures.some(feature => companyFeatures.includes(feature));
      }
    };
  }, [companyFeatures, canAccessItem, user]);

  // Function to check if user can access a navigation group
  const canAccessGroup = useMemo(() => {
    return (group: NavigationGroup): boolean => {
      // Group is accessible if user can access any of its items
      return group.items.some(item => canAccessItem(item));
    };
  }, [canAccessItem]);

  // Function to get access level for a specific item
  const getItemAccessLevel = useMemo(() => {
    return (itemId: string): 'none' | 'read' | 'write' => {
      if (userPermissions.length === 0) return 'none';

      // Find the item in navigation
      const allItems = [
        ...CORE_NAVIGATION,
        ...NAVIGATION_GROUPS.flatMap(g => g.items),
        ...QUICK_ACCESS_ITEMS
      ];
      const item = allItems.find(i => i.id === itemId);

      if (!item || !canAccessItem(item)) return 'none';

      // Check for write permissions
      const writeFeatures = item.requiredFeatures.filter((f: FeaturePermission) => f.endsWith('_write'));
      if (writeFeatures.length > 0 && hasAnyFeaturePermission(userPermissions, writeFeatures)) {
        return 'write';
      }

      // Check for read permissions
      const readFeatures = item.requiredFeatures.filter((f: FeaturePermission) => f.endsWith('_read'));
      if (readFeatures.length > 0 && hasAnyFeaturePermission(userPermissions, readFeatures)) {
        return 'read';
      }

      // If user can access but no specific read/write detected, assume read
      return 'read';
    };
  }, [userPermissions, canAccessItem]);

  // Filter accessible navigation groups (now includes locked items, but excludes items with hideFromSidebar)
  const accessibleGroups = useMemo(() => {
    return NAVIGATION_GROUPS
      .map(group => ({
        ...group,
        items: group.items
          .filter(item => !item.hideFromSidebar && (canAccessItem(item) || (item.showLock && isItemLocked(item))))
          .map(item => ({
            ...item,
            isLocked: isItemLocked(item),
            canAccess: canAccessItem(item) && !isItemLocked(item)
          }))
      }))
      .filter(group => group.items.length > 0)
      .sort((a, b) => a.order - b.order);
  }, [canAccessItem, isItemLocked]);

  // Get all accessible navigation items (flat)
  const accessibleItems = useMemo(() => {
    return accessibleGroups.flatMap(group => group.items) as NavigationItemWithLock[];
  }, [accessibleGroups]);

  // Filter core navigation
  const coreNavigation = useMemo(() => {
    return CORE_NAVIGATION
      .filter(item => canAccessItem(item) || (item.showLock && isItemLocked(item)))
      .map(item => ({
        ...item,
        isLocked: isItemLocked(item),
        canAccess: canAccessItem(item) && !isItemLocked(item)
      })) as NavigationItemWithLock[];
  }, [canAccessItem, isItemLocked]);

  // Filter quick access items
  const quickAccessItems = useMemo(() => {
    return QUICK_ACCESS_ITEMS
      .filter(item => canAccessItem(item) || (item.showLock && isItemLocked(item)))
      .map(item => ({
        ...item,
        isLocked: isItemLocked(item),
        canAccess: canAccessItem(item) && !isItemLocked(item)
      })) as NavigationItemWithLock[];
  }, [canAccessItem, isItemLocked]);

  // Get all items from groups (including hidden ones for search)
  const allGroupItems = useMemo(() => {
    return NAVIGATION_GROUPS
      .flatMap(group => group.items)
      .filter(item => canAccessItem(item) || (item.showLock && isItemLocked(item)))
      .map(item => ({
        ...item,
        isLocked: isItemLocked(item),
        canAccess: canAccessItem(item) && !isItemLocked(item)
      })) as NavigationItemWithLock[];
  }, [canAccessItem, isItemLocked]);

  // Get grouped navigation with access filtering
  const groupedNavigation = useMemo(() => {
    return accessibleGroups;
  }, [accessibleGroups]);

  // Get flat navigation list (includes all group items, even those hidden from sidebar)
  const flatNavigation = useMemo(() => {
    return [...coreNavigation, ...allGroupItems, ...quickAccessItems];
  }, [coreNavigation, allGroupItems, quickAccessItems]);

  // Total accessible items count
  const totalAccessibleItems = useMemo(() => {
    return flatNavigation.length;
  }, [flatNavigation]);

  return {
    // Filtered navigation data
    accessibleGroups,
    accessibleItems,
    coreNavigation,
    quickAccessItems,

    // Navigation state
    totalAccessibleItems,

    // Utility functions
    canAccessItem,
    canAccessGroup,
    getItemAccessLevel,

    // Navigation organization
    groupedNavigation,
    flatNavigation
  };
};

/**
 * Hook for checking specific navigation access
 */
export const useNavigationAccess = (itemId: string) => {
  const { canAccessItem, getItemAccessLevel } = useNavigation();

  const item = useMemo(() => {
    const allItems = [
      ...CORE_NAVIGATION,
      ...NAVIGATION_GROUPS.flatMap(g => g.items),
      ...QUICK_ACCESS_ITEMS
    ];
    return allItems.find(i => i.id === itemId);
  }, [itemId]);

  const canAccess = useMemo(() => {
    return item ? canAccessItem(item) : false;
  }, [item, canAccessItem]);

  const accessLevel = useMemo(() => {
    return getItemAccessLevel(itemId);
  }, [itemId, getItemAccessLevel]);

  return {
    item,
    canAccess,
    accessLevel,
    hasReadAccess: accessLevel !== 'none',
    hasWriteAccess: accessLevel === 'write'
  };
};

/**
 * Hook for navigation breadcrumbs
 */
export const useNavigationBreadcrumbs = (currentPath: string) => {
  const { flatNavigation } = useNavigation();

  const breadcrumbs = useMemo(() => {
    // Find matching navigation item
    const currentItem = flatNavigation.find(item =>
      currentPath === `/dashboard${item.href}` ||
      currentPath.startsWith(`/dashboard${item.href}/`)
    );

    if (!currentItem) return [];

    // Build breadcrumb trail
    const breadcrumbs = [
      { name: 'Dashboard', href: '/dashboard' }
    ];

    if (currentItem.href !== '') {
      breadcrumbs.push({
        name: currentItem.name,
        href: `/dashboard${currentItem.href}`
      });
    }

    return breadcrumbs;
  }, [currentPath, flatNavigation]);

  return breadcrumbs;
};

/**
 * Hook for navigation search/filtering
 */
export const useNavigationSearch = (searchTerm: string) => {
  const { flatNavigation } = useNavigation();

  const filteredNavigation = useMemo(() => {
    if (!searchTerm.trim()) return flatNavigation;

    const lowercaseSearch = searchTerm.toLowerCase();
    return flatNavigation.filter(item =>
      item.name.toLowerCase().includes(lowercaseSearch) ||
      item.description?.toLowerCase().includes(lowercaseSearch)
    );
  }, [searchTerm, flatNavigation]);

  return {
    filteredNavigation,
    hasResults: filteredNavigation.length > 0,
    totalResults: filteredNavigation.length
  };
};